import { ArrowDown, ArrowLeft, ArrowUp, Calculator, Check, ChevronDown, ChevronUp, Copy, ExternalLink, Info, PencilLine, TriangleAlert } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth'
import { MetricStrip } from '../components/MetricStrip'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { ProductTable } from '../components/ProductTable'
import { RealtimeProductAnalysisTable } from '../components/RealtimeProductAnalysisTable'
import { TrendChart } from '../components/TrendChart'
import { useProductData } from '../data'
import {
  accessoryFor,
  estimatedProfitForSku,
  internalProductCostOn,
  JD_STORE_ID,
  productMasterCostDefaults,
  productMasterRecordMap,
  profitWithRebateFor,
  rebateAmountForSku,
  rebateRateFor,
  resolvedProductCost,
  shippingProviderFor,
  unitOperatingCost,
} from '../productMaster'
import { internalProductRecordsByCode, summarizeProductProfit } from '../productProfit'
import {
  type RealtimeProductSortKey,
  realtimeMetricsToDaily,
  realtimeProductToRecord,
  useRealtimeData,
} from '../realtime'
import { badgesForProduct, skuMatchesTag } from '../segments'
import { statisticsPeriodOptions, statisticsWindow, type StatisticsPeriod, visibleDateRange } from '../statistics'
import type { DailyMetrics, ProductLevel, ShippingProvider, SortKey } from '../types'
import { deltaRate, integer, isActive, itemUnitPrice, metricsAcrossDates, metricsOn, money, percent, previousDateOf, rowToMetrics } from '../utils'

type TrendMetric = 'gmv' | 'profitWithRebate' | 'profitWithRebateMargin' | 'soldItemCount' | 'productVisitors' | 'conversionRate'
type DetailStatisticsPeriod = StatisticsPeriod | 'realtime'

const detailStatisticsPeriodOptions: Array<{ key: DetailStatisticsPeriod, label: string }> = [
  { key: 'realtime', label: '历史快照' },
  ...statisticsPeriodOptions,
]

const businessInsightProductUrl = (productId: string, level: ProductLevel) =>
  `https://jdsz.jd.com/szweb/view/single-product-analysis.html?id=${encodeURIComponent(productId)}&proType=${level}&from=productDetail`

const shippingProviderLabel = (provider: ShippingProvider) =>
  provider === 'jd-warehouse' ? '京东入仓' : provider === 'yto-express' ? '圆通快递' : '京东快递'

const trendOptions: Array<{ key: TrendMetric, label: string, formatter: (value: number) => string }> = [
  { key: 'gmv', label: '成交金额', formatter: (value) => value >= 10000 ? `${(value / 10000).toFixed(1)}万` : value.toFixed(0) },
  { key: 'profitWithRebate', label: '含返点利润', formatter: (value) => money(value) },
  { key: 'profitWithRebateMargin', label: '含返点利润率', formatter: (value) => percent(value * 100) },
  { key: 'soldItemCount', label: '成交件数', formatter: (value) => integer(value) },
  { key: 'productVisitors', label: '访客', formatter: (value) => integer(value) },
  { key: 'conversionRate', label: '转化率', formatter: (value) => `${value.toFixed(1)}%` },
]

function DayDelta({ current, previous }: { current: number, previous: number }) {
  const rate = deltaRate(current, previous)
  if (rate === null) return <span className="delta-positive"><ArrowUp aria-hidden="true" />新增</span>
  if (!rate) return <span className="delta-neutral">—</span>
  return <span className={rate > 0 ? 'delta-positive' : 'delta-negative'}>{rate > 0 ? <ArrowUp aria-hidden="true" /> : <ArrowDown aria-hidden="true" />}{Math.abs(rate).toFixed(2)}%</span>
}

export function ProductDetailPage() {
  const { level, id } = useParams<{ level: ProductLevel, id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const { canAccess } = useAuth()
  const { data, selectedDate, productIndex, segmentIndex, productMaster, internalProductInfo, erpInventory, saveSpuNameNote } = useProductData()
  const { data: realtimeData, loading: realtimeLoading, error: realtimeError, reload: reloadRealtime } = useRealtimeData()
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('gmv')
  const [noteEditing, setNoteEditing] = useState(false)
  const [noteDraft, setNoteDraft] = useState('')
  const [noteSaving, setNoteSaving] = useState(false)
  const [noteError, setNoteError] = useState('')
  useEffect(() => {
    setNoteEditing(false)
    setNoteError('')
  }, [level, id])
  const [statisticsPeriod, setStatisticsPeriod] = useState<DetailStatisticsPeriod>(() => {
    const requestedPeriod = searchParams.get('period')
    return detailStatisticsPeriodOptions.some((item) => item.key === requestedPeriod)
      ? requestedPeriod as DetailStatisticsPeriod
      : 'day'
  })
  const [skuSortKey, setSkuSortKey] = useState<SortKey>('gmv')
  const [realtimeSkuSortKey, setRealtimeSkuSortKey] = useState<RealtimeProductSortKey>('gmv')
  const [skuSortDirection, setSkuSortDirection] = useState<'asc' | 'desc'>('desc')
  const [expandedHistoryProductId, setExpandedHistoryProductId] = useState('')
  const [copiedIdKey, setCopiedIdKey] = useState('')
  const copyIdFeedbackTimer = useRef(0)
  const isRealtime = statisticsPeriod === 'realtime'
  const catalogProduct = level && id ? productIndex.get(`${level}:${id}`) : undefined
  const realtimeProduct = useMemo(
    () => level && id
      ? realtimeData?.products.find((item) => item.level === level && item.id === id)
      : undefined,
    [id, level, realtimeData],
  )
  const product = catalogProduct || (isRealtime && realtimeProduct
    ? realtimeProductToRecord(realtimeProduct)
    : undefined)

  useEffect(() => {
    const requestedPeriod = searchParams.get('period')
    setStatisticsPeriod(
      detailStatisticsPeriodOptions.some((item) => item.key === requestedPeriod)
        ? requestedPeriod as DetailStatisticsPeriod
        : 'day',
    )
  }, [searchParams])
  const productMasterRecords = useMemo(() => productMasterRecordMap(productMaster, JD_STORE_ID), [productMaster])
  const costDefaults = useMemo(() => productMasterCostDefaults(productMaster, JD_STORE_ID), [productMaster])
  const companyRecordsByCode = useMemo(
    () => internalProductRecordsByCode(internalProductInfo?.records || []),
    [internalProductInfo],
  )
  const erpAvailableByMerchantSku = useMemo(
    () => new Map((erpInventory?.records || []).map((record) => [record.productCode.trim(), record.available])),
    [erpInventory],
  )
  const sourceSkus = useMemo(() => {
    if (!data || !product) return []
    return product.level === 'sku'
      ? [product]
      : data.products.filter((item) => item.level === 'sku' && item.spuId === product.id)
  }, [data, product])
  const productBadges = useMemo(
    () => product ? badgesForProduct(segmentIndex, product, isRealtime ? realtimeData?.meta.businessDate || selectedDate : selectedDate) : [],
    [isRealtime, product, realtimeData?.meta.businessDate, segmentIndex, selectedDate],
  )

  const period = useMemo(
    () => data ? statisticsWindow(isRealtime ? 'day' : statisticsPeriod, selectedDate, data.meta.dates) : null,
    [data, isRealtime, selectedDate, statisticsPeriod],
  )

  const profitView = useMemo(() => {
    if (!data || !product || !period) return null
    const summary = summarizeProductProfit(
      sourceSkus,
      period.dates,
      selectedDate,
      productMasterRecords,
      costDefaults,
      companyRecordsByCode,
      segmentIndex,
    )
    const daily = new Map(data.meta.dates.map((date) => [
      date,
      summarizeProductProfit(
        sourceSkus,
        [date],
        selectedDate,
        productMasterRecords,
        costDefaults,
        companyRecordsByCode,
        segmentIndex,
      ),
    ]))
    const childProfitByProductId = new Map<string, number | null>()
    const childProfitMarginByProductId = new Map<string, number | null>()
    const childProfitContextByProductId = new Map<string, string>()
    for (const sku of sourceSkus) {
      const childSummary = summarizeProductProfit(
        [sku],
        period.dates,
        selectedDate,
        productMasterRecords,
        costDefaults,
        companyRecordsByCode,
        segmentIndex,
      )
      childProfitByProductId.set(sku.id, childSummary.profitWithRebate)
      childProfitMarginByProductId.set(sku.id, childSummary.profitWithRebateMargin)
      childProfitContextByProductId.set(sku.id, childSummary.context)
    }
    return { summary, daily, childProfitByProductId, childProfitMarginByProductId, childProfitContextByProductId }
  }, [
    companyRecordsByCode,
    costDefaults,
    data,
    period,
    product,
    productMasterRecords,
    segmentIndex,
    selectedDate,
    sourceSkus,
  ])

  const costFormula = useMemo(() => {
    if (!data || !product || product.level !== 'sku' || !period) return null
    const record = productMasterRecords.get(product.id)
    const companyRecords = companyRecordsByCode.get(product.merchantSku.trim()) || []
    const isAccessory = accessoryFor(product, record, companyRecords)
    const internalCost = internalProductCostOn(companyRecords, record, selectedDate)
    const inheritedJdWarehouse = skuMatchesTag(segmentIndex, product.id, 'jd-warehouse', period.dates)
    const shippingProvider = shippingProviderFor(
      record,
      inheritedJdWarehouse,
      selectedDate,
      costDefaults,
      isAccessory,
      internalCost.isBrandSpecial,
    )
    const giftExcluded = isAccessory || internalCost.isBrandSpecial
    const cost = resolvedProductCost(record, selectedDate, costDefaults, shippingProvider, giftExcluded)
    const estimate = estimatedProfitForSku(product, record, period.dates, costDefaults, companyRecords, shippingProvider)
    const rebateRate = rebateRateFor(isAccessory, internalCost.isBrandSpecial, internalCost.productCost)
    const rebateAmount = rebateAmountForSku(product, period.dates, companyRecords, isAccessory)
    const profitWithRebate = profitWithRebateFor(estimate.profit, rebateAmount)
    const unitCost = internalCost.productCost === null ? null : unitOperatingCost(cost, internalCost.productCost)
    const rateSum = cost.platformFeeRate + cost.taxRate
    return {
      isAccessory,
      internalCost,
      cost,
      giftExcluded,
      estimate,
      rebateRate,
      rebateAmount,
      profitWithRebate,
      unitCost,
      rateSum,
      unitCostStable: unitCost !== null && estimate.fulfillmentCost !== null
        && Math.abs(unitCost * estimate.metrics.soldItemCount - estimate.fulfillmentCost) < 0.005,
      rateStable: estimate.rateFee !== null
        && Math.abs(estimate.metrics.gmv * rateSum / 100 - estimate.rateFee) < 0.005,
    }
  }, [companyRecordsByCode, costDefaults, data, period, product, productMasterRecords, segmentIndex, selectedDate])

  const detail = useMemo(() => {
    if (!data || !product || !profitView) return null
    const history = product.history.map((row) => rowToMetrics(row)).sort((a, b) => b.date.localeCompare(a.date))
    const chart = history.slice().reverse().map((item) => {
      const daily = profitView.daily.get(item.date)
      return {
        date: item.date,
        value: trendMetric === 'profitWithRebate'
          ? daily?.profitWithRebate ?? null
          : trendMetric === 'profitWithRebateMargin'
            ? daily?.profitWithRebateMargin ?? null
            : item[trendMetric],
      }
    })
    return { history, chart }
  }, [data, product, profitView, trendMetric])

  const statistics = useMemo(() => {
    if (!data || !product || !period) return null
    if (isRealtime) {
      return {
        current: realtimeMetricsToDaily(realtimeProduct?.metrics || {}, realtimeData?.meta.businessDate || ''),
        previousDay: realtimeMetricsToDaily(realtimeProduct?.previousMetrics || {}, realtimeData?.meta.businessDate || ''),
      }
    }
    const previousDate = previousDateOf(data.meta.dates, selectedDate)
    return {
      current: metricsAcrossDates(product, period.dates),
      previousDay: metricsOn(product, previousDate),
    }
  }, [data, isRealtime, period, product, realtimeData?.meta.businessDate, realtimeProduct, selectedDate])

  const childSkuView = useMemo(() => {
    if (!data || !product || product.level !== 'spu' || !period) return { products: [], activeCount: 0, mappedCount: 0, badgesByProductId: new Map() }
    const mappedProducts = data.products.filter((item) => item.level === 'sku' && item.spuId === product.id)
    if (!period.dates.length) return { products: [], activeCount: 0, mappedCount: mappedProducts.length, badgesByProductId: new Map() }
    const previousDate = previousDateOf(data.meta.dates, selectedDate)
    const effectiveSortKey = statisticsPeriod === 'day' ? skuSortKey : skuSortKey === 'delta' ? 'gmv' : skuSortKey
    const rows = mappedProducts.map((item) => {
      const current = metricsAcrossDates(item, period.dates)
      const previous = metricsOn(item, previousDate)
      let value: number | null = null
      if (effectiveSortKey === 'profitWithRebate') {
        value = profitView?.childProfitByProductId.get(item.id) ?? null
      } else if (effectiveSortKey === 'profitWithRebateMargin') {
        value = profitView?.childProfitMarginByProductId.get(item.id) ?? null
      } else if (effectiveSortKey === 'delta') {
        value = current.gmv - previous.gmv
      } else if (effectiveSortKey === 'averageItemPrice') {
        value = itemUnitPrice(current.gmv, current.soldItemCount) || 0
      } else {
        value = current[effectiveSortKey]
      }
      return { item, current, value }
    })
    rows.sort((a, b) => {
      const aMissing = a.value === null || a.value === undefined
      const bMissing = b.value === null || b.value === undefined
      if (aMissing && bMissing) return a.item.id.localeCompare(b.item.id)
      if (aMissing) return 1
      if (bMissing) return -1
      const result = (a.value as number) - (b.value as number)
      if (result) return skuSortDirection === 'asc' ? result : -result
      return a.item.id.localeCompare(b.item.id)
    })
    return {
      products: rows.map((row) => row.item),
      activeCount: rows.filter((row) => isActive(row.current)).length,
      mappedCount: mappedProducts.length,
      badgesByProductId: new Map(mappedProducts.map((item) => [item.id, badgesForProduct(segmentIndex, item, selectedDate)])),
    }
  }, [data, period, product, profitView, segmentIndex, selectedDate, skuSortDirection, skuSortKey, statisticsPeriod])

  const realtimeChildSkuView = useMemo(() => {
    if (!isRealtime || !product || product.level !== 'spu' || !realtimeData) {
      return { products: [], activeCount: 0 }
    }
    const products = realtimeData.products
      .filter((item) => item.level === 'sku' && item.spuId === product.id)
      .sort((a, b) => {
        const value = (item: typeof a) => realtimeSkuSortKey === 'delta'
          ? item.metrics.gmv - (item.previousMetrics?.gmv || 0)
          : item.metrics[realtimeSkuSortKey]
        const result = Number(value(a) || 0) - Number(value(b) || 0)
        if (result) return skuSortDirection === 'asc' ? result : -result
        return a.id.localeCompare(b.id)
      })
    return {
      products,
      activeCount: products.filter((item) => item.metrics.gmv > 0 || item.metrics.soldItemCount > 0).length,
    }
  }, [isRealtime, product, realtimeData, realtimeSkuSortKey, skuSortDirection])

  if (!data) return null
  if (!product || !detail || !period || !statistics || !profitView) return <div className="not-found"><h1>未找到商品</h1><Link to={`/${level === 'sku' ? 'sku' : 'spu'}`}>返回商品列表</Link></div>
  const option = trendOptions.find((item) => item.key === trendMetric) || trendOptions[0]
  const linkedSku = product.jdSkuId ? productIndex.get(`sku:${product.jdSkuId}`) : undefined
  const copyIdValue = async (value: string, feedbackKey: string) => {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      const input = document.createElement('textarea')
      input.value = value
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      input.remove()
    }
    setCopiedIdKey(feedbackKey)
    window.clearTimeout(copyIdFeedbackTimer.current)
    copyIdFeedbackTimer.current = window.setTimeout(() => setCopiedIdKey(''), 1500)
  }
  const copyIdButton = (value: string, feedbackKey: string, label: string) => (
    <button
      type="button"
      className={`sku-copy-button${copiedIdKey === feedbackKey ? ' is-copied' : ''}`}
      title={copiedIdKey === feedbackKey ? '已复制' : `复制${label}`}
      aria-label={`复制${label} ${value}`}
      onClick={() => void copyIdValue(value, feedbackKey)}
    >
      {copiedIdKey === feedbackKey ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
    </button>
  )
  const historyExpanded = expandedHistoryProductId === product.id
  const visibleHistory = historyExpanded ? detail.history : detail.history.slice(0, 7)
  const hiddenHistoryCount = Math.max(0, detail.history.length - 7)
  const context = (current: number, previous: number) => {
    const rate = deltaRate(current, previous)
    return rate === null ? '较前日新增' : `较前日 ${rate >= 0 ? '+' : ''}${rate.toFixed(2)}%`
  }
  const aggregateContext = period.complete
    ? `${period.dates.length}天分天汇总`
    : `${period.dates.length}/${period.requestedDayCount}天可用`
  const effectiveSortKey = statisticsPeriod === 'day' ? skuSortKey : skuSortKey === 'delta' ? 'gmv' : skuSortKey
  const latestRealtimeSnapshot = realtimeData?.snapshots[realtimeData.snapshots.length - 1]
  const previousRealtimeSnapshot = realtimeData && realtimeData.snapshots.length > 1
    ? realtimeData.snapshots[realtimeData.snapshots.length - 2]
    : null
  const realtimeSnapshotTime = latestRealtimeSnapshot
    ? new Date(latestRealtimeSnapshot.snapshotAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
    : '等待数据'
  const realtimePreviousTime = previousRealtimeSnapshot
    ? new Date(previousRealtimeSnapshot.snapshotAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
    : ''
  const realtimeContext = (current: number, previous?: number | null, suffix = '') => {
    if (!realtimeProduct?.previousMetrics || previous === null || previous === undefined) return '首份快照，暂无时段变化'
    const delta = current - previous
    return `较 ${realtimePreviousTime} ${delta > 0 ? '+' : ''}${suffix === '%' ? `${delta.toFixed(2)}pt` : suffix === '¥' ? money(delta) : integer(delta)}`
  }
  const handleSkuSort = (key: SortKey) => {
    if (key === skuSortKey) setSkuSortDirection((direction) => direction === 'desc' ? 'asc' : 'desc')
    else { setSkuSortKey(key); setSkuSortDirection('desc') }
  }
  const handleRealtimeSkuSort = (key: RealtimeProductSortKey) => {
    if (key === realtimeSkuSortKey) setSkuSortDirection((direction) => direction === 'desc' ? 'asc' : 'desc')
    else { setRealtimeSkuSortKey(key); setSkuSortDirection('desc') }
  }
  const handleStatisticsPeriod = (value: DetailStatisticsPeriod) => {
    setStatisticsPeriod(value)
    const nextParams = new URLSearchParams(searchParams)
    if (value === 'day') nextParams.delete('period')
    else nextParams.set('period', value)
    setSearchParams(nextParams, { replace: true })
    if (value === 'realtime') {
      setRealtimeSkuSortKey('gmv')
      setSkuSortDirection('desc')
    } else if (value !== 'day' && skuSortKey === 'delta') {
      setSkuSortKey('gmv')
      setSkuSortDirection('desc')
    }
  }
  const periodSearchSuffix = statisticsPeriod === 'day' ? '' : `?period=${encodeURIComponent(statisticsPeriod)}`
  const startNoteEditing = () => {
    setNoteDraft(product.nameNote || '')
    setNoteError('')
    setNoteEditing(true)
  }
  const submitNote = async () => {
    if (noteSaving) return
    setNoteSaving(true)
    setNoteError('')
    try {
      await saveSpuNameNote(product.id, noteDraft.trim())
      setNoteEditing(false)
    } catch (reason) {
      setNoteError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setNoteSaving(false)
    }
  }
  return (
    <div className="detail-page">
      <div className="breadcrumb-row"><Link to={`/${product.level}${periodSearchSuffix}`}><ArrowLeft aria-hidden="true" />返回</Link><span>{product.level.toUpperCase()}</span><span>/</span><span>{product.nameNote || product.name}</span></div>
      <section className="product-identity">
        <figure>
          {product.image ? <img src={product.image} alt={product.name} /> : <span className="detail-image-fallback">暂无匹配图片</span>}
          <figcaption>{product.image ? product.imageLabel : '无官方图片映射'}</figcaption>
        </figure>
        <div className="identity-copy">
          <h1>{product.nameNote || product.name}</h1>
          {product.nameNote && <p className="identity-source-name">{product.name}</p>}
          {product.level === 'spu' && catalogProduct && !noteEditing && (
            <div className="name-note-row">
              <button type="button" className="name-note-button" onClick={startNoteEditing}>
                <PencilLine aria-hidden="true" />
                {product.nameNote ? '编辑名称备注' : '添加名称备注'}
              </button>
              {!product.nameNote && <span className="name-note-hint">备注用于一眼识别款式，官方标题保留用于搜索</span>}
            </div>
          )}
          {product.level === 'spu' && catalogProduct && noteEditing && (
            <div className="name-note-editor">
              <input
                value={noteDraft}
                maxLength={60}
                autoFocus
                placeholder="输入便于识别的备注名，留空表示清除"
                onChange={(event) => setNoteDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void submitNote()
                  if (event.key === 'Escape') setNoteEditing(false)
                }}
              />
              <button type="button" className="name-note-save" disabled={noteSaving} onClick={() => void submitNote()}>
                {noteSaving ? '保存中…' : '保存'}
              </button>
              <button type="button" className="name-note-cancel" disabled={noteSaving} onClick={() => setNoteEditing(false)}>取消</button>
              {noteError && <span className="name-note-error">{noteError}</span>}
            </div>
          )}
          {!!productBadges.length && (
            <div className="identity-tag-row">
              {productBadges.map((badge) => <span key={badge.id} className={`tag-badge tag-${badge.tone}`} title={badge.title}>{badge.label}</span>)}
            </div>
          )}
          <dl>
            <div><dt>SPU</dt><dd>{product.spuId ? <>{product.spuId}{copyIdButton(product.spuId, 'spu', 'SPU')}</> : '—'}</dd></div>
            <div>
              <dt>{product.level === 'spu' ? '代表SKU' : '京东SKU'}</dt>
              <dd>
                {product.jdSkuId
                  ? <>
                      {product.level === 'spu' && linkedSku
                        ? <Link to={`/product/sku/${product.jdSkuId}${periodSearchSuffix}`}>{product.jdSkuId}</Link>
                        : product.jdSkuId}
                      {copyIdButton(product.jdSkuId, 'jdSku', '京东SKU')}
                    </>
                  : '—'}
              </dd>
            </div>
            <div><dt>商家SKU</dt><dd>{product.merchantSku ? <>{product.merchantSku}{copyIdButton(product.merchantSku, 'merchantSku', '商家SKU')}</> : '—'}</dd></div>
            {product.level === 'spu' && (
              <div>
                <dt>旗下SKU</dt>
                <dd>{isRealtime
                  ? `${realtimeChildSkuView.products.length.toLocaleString('zh-CN')} 个（实时可确认映射）`
                  : `${childSkuView.mappedCount.toLocaleString('zh-CN')} 个（官方映射）`}
                </dd>
              </div>
            )}
          </dl>
          <div className="identity-actions" aria-label={`${product.level === 'spu' ? 'SPU' : 'SKU'} 快捷操作`}>
            {product.level === 'sku' && canAccess(JD_STORE_ID, 'jd.profit_calculator') && (
              <Link
                className="identity-profit-calculator-link"
                to={`/jd/profit-calculator?sku=${encodeURIComponent(product.jdSkuId || product.id)}`}
                title="带入当前京东SKU开始利润测算"
              >
                <Calculator aria-hidden="true" />
                发送到利润计算器
              </Link>
            )}
            <a href={businessInsightProductUrl(product.id, product.level)} target="_blank" rel="noreferrer">
              商智 {product.level === 'spu' ? 'SPU' : 'SKU'} 分析
              <ExternalLink aria-hidden="true" />
            </a>
            {product.productUrl
              ? (
                  <a href={product.productUrl} target="_blank" rel="noreferrer">
                    京东商品销售页
                    <ExternalLink aria-hidden="true" />
                  </a>
                )
              : <span className="identity-action-unavailable">暂无官方销售链接</span>}
          </div>
        </div>
      </section>

      <section className="detail-section">
        <div className="section-heading-row period-heading">
          <div>
            <h2>统计表现</h2>
            <p>{isRealtime
              ? `${realtimeData?.meta.businessDate || '暂无日期'} ${realtimeSnapshotTime} · ${realtimeData?.meta.snapshotCount || 0} 份只读快照`
              : `${period.label} · ${visibleDateRange(period.dates)} · ${period.complete ? `完整${period.requestedDayCount}天` : `仅覆盖${period.dates.length}/${period.requestedDayCount}天`}`}
            </p>
          </div>
          <div className="section-heading-meta">
            <DataFreshnessBadge
              date={isRealtime ? realtimeData?.meta.businessDate : data.meta.maxDate}
              label={isRealtime ? '快照数据截至' : '商品数据截至'}
              mode={isRealtime ? 'paused' : 'daily'}
            />
            <div className="metric-switch" role="group" aria-label="统计时间范围">
              {detailStatisticsPeriodOptions.map((item) => (
                <button
                  type="button"
                  key={item.key}
                  className={item.key === statisticsPeriod ? 'is-active' : ''}
                  aria-pressed={item.key === statisticsPeriod}
                  onClick={() => handleStatisticsPeriod(item.key)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {isRealtime && (
          <div className="realtime-list-notices">
            <p className="realtime-inline-notice">
              <Info aria-hidden="true" />
              历史实时快照已暂停更新，不代表当前经营情况；需要新数据时，在频道说一声由AI按需采集。利润、退款、成本与30天趋势继续使用完整自然日口径。
              <button type="button" onClick={() => void reloadRealtime()}>刷新本地快照</button>
            </p>
            {product.level === 'sku' && realtimeData?.meta.skuExportLimitReached && (
              <p className="realtime-inline-warning">
                <TriangleAlert aria-hidden="true" />
                SKU实时导出达到京东1000行上限；未出现在快照中不能判断为零成交。
              </p>
            )}
            {realtimeError && <p className="realtime-inline-warning">{realtimeError}</p>}
            {realtimeLoading && !realtimeData && <p className="realtime-inline-notice">正在加载实时商品快照…</p>}
            {!realtimeLoading && realtimeData && !realtimeProduct && (
              <p className="realtime-inline-warning">
                <TriangleAlert aria-hidden="true" />
                当前实时导出未包含该商品，因此不渲染零值指标。
              </p>
            )}
          </div>
        )}
        {!isRealtime && !period.complete && (
          <p className="period-coverage-warning">
            请求范围为 {period.startDate} 至 {period.endDate}，当前滚动数据只能覆盖 {visibleDateRange(period.dates)}；以下为部分区间汇总。
          </p>
        )}
        {isRealtime
          ? realtimeProduct && (
              <MetricStrip items={[
                { label: '实时成交金额', value: money(statistics.current.gmv), context: realtimeContext(statistics.current.gmv, realtimeProduct.previousMetrics?.gmv, '¥') },
                { label: '成交件数', value: integer(statistics.current.soldItemCount), context: realtimeContext(statistics.current.soldItemCount, realtimeProduct.previousMetrics?.soldItemCount) },
                { label: '商品访客', value: integer(statistics.current.productVisitors), context: realtimeContext(statistics.current.productVisitors, realtimeProduct.previousMetrics?.productVisitors) },
                { label: '浏览量', value: integer(statistics.current.productPageviews), context: realtimeContext(statistics.current.productPageviews, realtimeProduct.previousMetrics?.productPageviews) },
                { label: '转化率', value: percent(statistics.current.conversionRate), context: realtimeContext(statistics.current.conversionRate, realtimeProduct.previousMetrics?.conversionRate, '%') },
                { label: '客单价', value: money(statistics.current.averageOrderValue), context: '实时成交金额 ÷ 实时成交客户' },
              ]} />
            )
          : (
              <MetricStrip items={[
                { label: '成交金额', value: money(statistics.current.gmv), context: statisticsPeriod === 'day' ? context(statistics.current.gmv, statistics.previousDay.gmv) : aggregateContext },
                {
                  label: '含返点利润',
                  value: profitView.summary.activeSkuCount === 0 || profitView.summary.coveredSkuCount > 0
                    ? money(profitView.summary.profitWithRebate || 0)
                    : '—',
                  context: profitView.summary.context,
                },
                {
                  label: '含返点利润率',
                  value: profitView.summary.profitWithRebateMargin === null
                    ? '—'
                    : percent(profitView.summary.profitWithRebateMargin * 100),
                  context: '含返点利润 ÷ 已覆盖成交金额',
                },
                { label: '成交件数', value: integer(statistics.current.soldItemCount), context: statisticsPeriod === 'day' ? context(statistics.current.soldItemCount, statistics.previousDay.soldItemCount) : aggregateContext },
                { label: '访客', value: integer(statistics.current.productVisitors), context: statisticsPeriod === 'day' ? context(statistics.current.productVisitors, statistics.previousDay.productVisitors) : '按分天访客累加' },
                { label: '转化率', value: percent(statistics.current.conversionRate), context: statisticsPeriod === 'day' ? `${statistics.previousDay.conversionRate.toFixed(2)}% → ${statistics.current.conversionRate.toFixed(2)}%` : '累计成交客户 ÷ 累计访客' },
                { label: '客单价', value: money(statistics.current.averageOrderValue), context: statisticsPeriod === 'day' ? undefined : '累计成交金额 ÷ 累计成交客户' },
                { label: '取消退款', value: money(statistics.current.cancelRefundAmount), context: statisticsPeriod === 'day' ? undefined : aggregateContext },
              ]} />
            )}
      </section>

      {!isRealtime && product.level === 'sku' && costFormula && (
        <section className="detail-section">
          <div className="section-heading-row">
            <div>
              <h2>成本与利润计算</h2>
              <p>{period.label} · {visibleDateRange(period.dates)} · 费用参数为业务日期 {selectedDate} 生效版本</p>
            </div>
          </div>
          <article className="profit-formula-card profit-formula-single">
            <h3>本期利润代入</h3>
            {costFormula.estimate.activeDates === 0 && <p className="formula-empty">本期无成交，未产生成本与利润；下表为该SKU当前成本参数。</p>}
            {costFormula.estimate.activeDates > 0 && costFormula.estimate.profit === null && (
              <p className="formula-empty profit-missing">本期有成交，但产品成本缺失，无法计算利润；请在商品资料中关联“商家SKU ↔ 产品编码”。</p>
            )}
            <table className="unit-cost-table">
              <thead>
                <tr>
                  <th>项目</th>
                  <th>单价 / 费率</th>
                  <th>数量 / 基数</th>
                  <th className="cost-value">金额</th>
                </tr>
              </thead>
              {costFormula.estimate.profit !== null
                ? (
                    <tbody>
                      <tr>
                        <td>成交金额</td>
                        <td className="cost-note">
                          {costFormula.estimate.metrics.soldItemCount > 0
                            ? `客单 ${money(costFormula.estimate.metrics.gmv / costFormula.estimate.metrics.soldItemCount)}`
                            : '—'}
                        </td>
                        <td className="cost-note">{integer(costFormula.estimate.metrics.soldItemCount)} 件</td>
                        <td className="cost-value">{money(costFormula.estimate.metrics.gmv)}</td>
                      </tr>
                      {costFormula.unitCostStable
                        ? [
                            { label: costFormula.internalCost.isBrandSpecial ? '产品成本（品牌特价）' : '产品成本（总代价）', unit: costFormula.internalCost.productCost || 0 },
                            { label: '包装费', unit: costFormula.cost.packagingCost },
                            { label: `运费（${shippingProviderLabel(costFormula.cost.shippingProvider)}）`, unit: costFormula.cost.shippingFee },
                            {
                              label: costFormula.giftExcluded
                                ? costFormula.isAccessory ? '赠品费（配件不计）' : '赠品费（品牌特价不计）'
                                : '赠品费',
                              unit: costFormula.cost.giftCost,
                            },
                            { label: '运费险', unit: costFormula.cost.shippingInsuranceFee },
                          ].map((row) => (
                            <tr key={row.label}>
                              <td>{row.label}</td>
                              <td className="cost-note">{money(row.unit)}</td>
                              <td className="cost-note">× {integer(costFormula.estimate.metrics.soldItemCount)} 件</td>
                              <td className="cost-value profit-negative">−{money(row.unit * costFormula.estimate.metrics.soldItemCount)}</td>
                            </tr>
                          ))
                        : (
                            <tr>
                              <td>履约成本</td>
                              <td className="cost-note">分天费用版本汇总</td>
                              <td className="cost-note">× {integer(costFormula.estimate.metrics.soldItemCount)} 件</td>
                              <td className="cost-value profit-negative">−{money(costFormula.estimate.fulfillmentCost || 0)}</td>
                            </tr>
                          )}
                      {costFormula.rateStable
                        ? (
                            <>
                              <tr>
                                <td>平台扣点</td>
                                <td className="cost-note">{percent(costFormula.cost.platformFeeRate)}</td>
                                <td className="cost-note">× {money(costFormula.estimate.metrics.gmv)}</td>
                                <td className="cost-value profit-negative">−{money(costFormula.estimate.metrics.gmv * costFormula.cost.platformFeeRate / 100)}</td>
                              </tr>
                              <tr>
                                <td>税点</td>
                                <td className="cost-note">{percent(costFormula.cost.taxRate)}</td>
                                <td className="cost-note">× {money(costFormula.estimate.metrics.gmv)}</td>
                                <td className="cost-value profit-negative">−{money(costFormula.estimate.metrics.gmv * costFormula.cost.taxRate / 100)}</td>
                              </tr>
                            </>
                          )
                        : (
                            <tr>
                              <td>平台扣点＋税点</td>
                              <td className="cost-note">分天费率汇总</td>
                              <td className="cost-note">× {money(costFormula.estimate.metrics.gmv)}</td>
                              <td className="cost-value profit-negative">−{money(costFormula.estimate.rateFee || 0)}</td>
                            </tr>
                          )}
                      <tr className="unit-cost-total">
                        <td>＝ 预估利润</td>
                        <td className="cost-note" colSpan={2}>成交金额 − 履约成本 − 扣点税点</td>
                        <td className={`cost-value ${costFormula.estimate.profit >= 0 ? 'profit-positive' : 'profit-negative'}`}>{money(costFormula.estimate.profit)}</td>
                      </tr>
                      <tr>
                        <td>返点金额</td>
                        <td className="cost-note">
                          {costFormula.rebateRate !== null
                            ? `${money(costFormula.internalCost.productCost || 0)} × ${percent(costFormula.rebateRate * 100)}`
                            : costFormula.isAccessory ? '配件不计返点' : '品牌特价不计返点'}
                        </td>
                        <td className="cost-note">{costFormula.rebateRate !== null ? `× ${integer(costFormula.estimate.metrics.soldItemCount)} 件` : '—'}</td>
                        <td className="cost-value">{costFormula.rebateRate !== null ? `＋${money(costFormula.rebateAmount || 0)}` : money(0)}</td>
                      </tr>
                      <tr className="unit-cost-total calc-major">
                        <td>＝ 含返点利润</td>
                        <td className="cost-note" colSpan={2}>
                          利润率 {costFormula.estimate.metrics.gmv > 0 && costFormula.profitWithRebate !== null
                            ? percent(costFormula.profitWithRebate / costFormula.estimate.metrics.gmv * 100)
                            : '—'}（利润 ÷ 成交金额）
                        </td>
                        <td className={`cost-value ${(costFormula.profitWithRebate || 0) >= 0 ? 'profit-positive' : 'profit-negative'}`}>{money(costFormula.profitWithRebate || 0)}</td>
                      </tr>
                    </tbody>
                  )
                : (
                    <tbody>
                      <tr>
                        <td>{costFormula.internalCost.isBrandSpecial ? '产品成本（品牌特价）' : '产品成本（总代价）'}</td>
                        <td className="cost-note">
                          {costFormula.internalCost.productCost === null
                            ? <span className="profit-missing">{costFormula.internalCost.issue ? '价格冲突' : '缺失'}</span>
                            : money(costFormula.internalCost.productCost)}
                        </td>
                        <td className="cost-note">× 成交件数</td>
                        <td className="cost-value">—</td>
                      </tr>
                      {[
                        { label: '包装费', unit: costFormula.cost.packagingCost },
                        { label: `运费（${shippingProviderLabel(costFormula.cost.shippingProvider)}）`, unit: costFormula.cost.shippingFee },
                        {
                          label: costFormula.giftExcluded
                            ? costFormula.isAccessory ? '赠品费（配件不计）' : '赠品费（品牌特价不计）'
                            : '赠品费',
                          unit: costFormula.cost.giftCost,
                        },
                        { label: '运费险', unit: costFormula.cost.shippingInsuranceFee },
                      ].map((row) => (
                        <tr key={row.label}>
                          <td>{row.label}</td>
                          <td className="cost-note">{money(row.unit)}</td>
                          <td className="cost-note">× 成交件数</td>
                          <td className="cost-value">—</td>
                        </tr>
                      ))}
                      <tr>
                        <td>平台扣点</td>
                        <td className="cost-note">{percent(costFormula.cost.platformFeeRate)}</td>
                        <td className="cost-note">× 成交金额</td>
                        <td className="cost-value">—</td>
                      </tr>
                      <tr>
                        <td>税点</td>
                        <td className="cost-note">{percent(costFormula.cost.taxRate)}</td>
                        <td className="cost-note">× 成交金额</td>
                        <td className="cost-value">—</td>
                      </tr>
                      <tr>
                        <td>返点</td>
                        <td className="cost-note">
                          {costFormula.rebateRate !== null
                            ? percent(costFormula.rebateRate * 100)
                            : costFormula.isAccessory
                              ? '配件不计返点'
                              : costFormula.internalCost.isBrandSpecial ? '品牌特价不计返点' : '—'}
                        </td>
                        <td className="cost-note">× 产品成本 × 成交件数</td>
                        <td className="cost-value">—</td>
                      </tr>
                    </tbody>
                  )}
            </table>
          </article>
          <p className="methodology-note">
            预估利润 ＝ 成交金额 − 成交件数 × (产品成本＋包装＋运费＋赠品＋运费险) − 成交金额 × (平台扣点＋税点)；含返点利润 ＝ 预估利润 ＋ 产品成本 × 返点率 × 成交件数。
            所有时间范围统一使用内部表当前最新产品成本（品牌特价优先，其次总代价）；配件与品牌特价SKU不计赠品、固定圆通快递且不计返点；费用版本按分天生效日期取值。
          </p>
        </section>
      )}

      {!isRealtime && (
        <>
          <section className="detail-section chart-section">
            <div className="section-heading-row"><h2>30天趋势</h2><div className="metric-switch" role="group" aria-label="趋势指标">{trendOptions.map((item) => <button type="button" key={item.key} className={item.key === trendMetric ? 'is-active' : ''} onClick={() => setTrendMetric(item.key)}>{item.label}</button>)}</div></div>
            <TrendChart data={detail.chart} valueFormatter={option.formatter} ariaLabel={`${product.name}${option.label}30天趋势`} />
          </section>

          <section className="detail-section">
            <h2>每日明细</h2>
            <div className="table-responsive">
              <table className="history-table">
                <thead><tr><th>日期</th><th>成交金额</th><th>含返点利润</th><th>含返点利润率</th><th>成交件数</th><th>访客</th><th>转化率</th><th>客单价</th><th>取消退款</th><th>较前日</th></tr></thead>
                <tbody>{visibleHistory.map((item, index) => {
                  const previous = detail.history[index + 1] || ({} as DailyMetrics)
                  const dailyProfit = profitView.daily.get(item.date)
                  return <tr key={item.date} className={item.date === selectedDate ? 'is-selected-date' : ''}><td>{item.date}</td><td>{money(item.gmv)}</td><td className={dailyProfit?.profitWithRebate === null ? 'profit-missing' : (dailyProfit?.profitWithRebate || 0) >= 0 ? 'profit-positive' : 'profit-negative'} title={dailyProfit?.context}>{dailyProfit?.profitWithRebate === null || dailyProfit?.profitWithRebate === undefined ? '—' : money(dailyProfit.profitWithRebate)}</td><td className={dailyProfit?.profitWithRebateMargin === null ? 'profit-missing' : (dailyProfit?.profitWithRebateMargin || 0) >= 0 ? 'profit-positive' : 'profit-negative'} title={dailyProfit?.context}>{dailyProfit?.profitWithRebateMargin === null || dailyProfit?.profitWithRebateMargin === undefined ? '—' : percent(dailyProfit.profitWithRebateMargin * 100)}</td><td>{integer(item.soldItemCount)}</td><td>{integer(item.productVisitors)}</td><td>{percent(item.conversionRate)}</td><td>{money(item.averageOrderValue)}</td><td>{money(item.cancelRefundAmount)}</td><td><DayDelta current={item.gmv} previous={previous.gmv || 0} /></td></tr>
                })}</tbody>
              </table>
            </div>
            {hiddenHistoryCount > 0 && (
              <div className="history-toggle-row">
                <button
                  type="button"
                  className="history-toggle-button"
                  aria-expanded={historyExpanded}
                  onClick={() => setExpandedHistoryProductId(historyExpanded ? '' : product.id)}
                >
                  {historyExpanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
                  {historyExpanded ? '收起，仅显示最近 7 天' : `展开剩余 ${hiddenHistoryCount} 天`}
                </button>
              </div>
            )}
          </section>
        </>
      )}

      {product.level === 'spu' && (
        <section className="detail-section">
          <div className="section-heading-row">
            <div>
              <h2>旗下 SKU 销售明细</h2>
              <p>{isRealtime
                ? `历史快照 ${realtimeSnapshotTime} · 可确认官方映射 ${realtimeChildSkuView.products.length.toLocaleString('zh-CN')} 个 SKU · 快照动销 ${realtimeChildSkuView.activeCount.toLocaleString('zh-CN')} 个`
                : `${period.label} · ${visibleDateRange(period.dates)} · 官方映射 ${childSkuView.mappedCount.toLocaleString('zh-CN')} 个 SKU · 区间动销 ${childSkuView.activeCount.toLocaleString('zh-CN')} 个`}
              </p>
            </div>
            <div className="section-heading-meta">
              <DataFreshnessBadge date={erpInventory?.source.exportedAt} label="ERP库存截至" mode="on-demand" compact />
              <span>销售额=成交金额 · 销量=成交件数</span>
            </div>
          </div>
          {isRealtime ? (
            <RealtimeProductAnalysisTable
              products={realtimeChildSkuView.products}
              productRecords={productIndex}
              sortKey={realtimeSkuSortKey}
              sortDirection={skuSortDirection}
              onSort={handleRealtimeSkuSort}
              compact
              showAverageItemPrice
              erpAvailableByMerchantSku={erpAvailableByMerchantSku}
              emptyMessage="当前实时SKU文件没有可确认归属于该SPU的行；不按名称猜测父子关系。"
            />
          ) : (
            <ProductTable
              products={childSkuView.products}
              selectedDate={selectedDate}
              dates={data.meta.dates}
              sortKey={effectiveSortKey}
              sortDirection={skuSortDirection}
              onSort={handleSkuSort}
              compact
              metricDates={period.dates}
              showDelta={statisticsPeriod === 'day'}
              showAverageItemPrice
              badgesByProductId={childSkuView.badgesByProductId}
              profitByProductId={profitView.childProfitByProductId}
              profitMarginByProductId={profitView.childProfitMarginByProductId}
              profitContextByProductId={profitView.childProfitContextByProductId}
              erpAvailableByMerchantSku={erpAvailableByMerchantSku}
              detailSearch={periodSearchSuffix}
              emptyMessage={!period.dates.length
                ? '所选统计范围暂无可用数据。'
                : '暂无可确认的官方 SPU→SKU 映射，不按商品名称猜测。'}
            />
          )}
          <p className="methodology-note">{isRealtime
            ? '仅展示实时SKU文件中具有官方SPU映射的行；SKU导出达到1000行上限，未映射或未导出的SKU不归入任何SPU，也不按名称猜测。'
            : '所选区间按商智分天数据汇总：成交金额、件数、访客和成交客户按天累加，件单价=成交金额÷成交件数，转化率=累计成交客户÷累计访客。仅展示官方父子映射；未映射历史SKU不归入任何SPU，因此子表汇总可能与SPU汇总不同。'}
          </p>
        </section>
      )}
    </div>
  )
}
